import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Sparkles, Edit2, Save, X, Target, MessageSquare, Building2, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useState, useEffect } from "react";
import type { CompanyProfile } from "@shared/schema";
import { useIsHotelEdition } from "@/lib/hotelEdition";

export default function MarketingSetup() {
  const { toast } = useToast();

  const { data: company, isLoading } = useQuery<CompanyProfile>({
    queryKey: ['/api/companies/me']
  });
  // Hotel-Edition (PLAN §5.1 item 5): hotel wording; the team fills these texts when it sets the hotel up.
  const isHotel = useIsHotelEdition();

  const [isEditingBusinessOverview, setIsEditingBusinessOverview] = useState(false);
  const [isEditingValueProposition, setIsEditingValueProposition] = useState(false);
  const [isEditingTargetAudience, setIsEditingTargetAudience] = useState(false);
  
  const [tempBusinessOverview, setTempBusinessOverview] = useState("");
  const [tempValueProposition, setTempValueProposition] = useState("");
  const [tempTargetAudience, setTempTargetAudience] = useState("");

  // A background refresh of the company (hotel capability polling) must not overwrite a field being edited.
  useEffect(() => {
    if (company) {
      if (!isEditingBusinessOverview) setTempBusinessOverview(company.businessOverview || "");
      if (!isEditingValueProposition) setTempValueProposition(company.valueProposition || "");
      if (!isEditingTargetAudience) setTempTargetAudience(company.targetAudience || "");
    }
  }, [company, isEditingBusinessOverview, isEditingValueProposition, isEditingTargetAudience]);

  const updateAIFieldMutation = useMutation({
    mutationFn: async (data: Partial<{ businessOverview: string; valueProposition: string; targetAudience: string }>) => {
      return await apiRequest("PATCH", "/api/companies/me/ai-fields", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/companies/me'] });
      toast({
        title: "Gespeichert",
        description: "Die Änderungen wurden erfolgreich gespeichert.",
      });
    },
    onError: () => {
      toast({
        title: "Fehler",
        description: "Die Änderungen konnten nicht gespeichert werden.",
        variant: "destructive",
      });
    },
  });

  const handleEditBusinessOverview = () => {
    setTempBusinessOverview(company?.businessOverview || "");
    setIsEditingBusinessOverview(true);
  };

  const handleSaveBusinessOverview = () => {
    updateAIFieldMutation.mutate({ businessOverview: tempBusinessOverview }, {
      onSuccess: () => {
        setIsEditingBusinessOverview(false);
      }
    });
  };

  const handleCancelBusinessOverview = () => {
    setIsEditingBusinessOverview(false);
    setTempBusinessOverview(company?.businessOverview || "");
  };

  const handleEditValueProposition = () => {
    setTempValueProposition(company?.valueProposition || "");
    setIsEditingValueProposition(true);
  };

  const handleSaveValueProposition = () => {
    updateAIFieldMutation.mutate({ valueProposition: tempValueProposition }, {
      onSuccess: () => {
        setIsEditingValueProposition(false);
      }
    });
  };

  const handleCancelValueProposition = () => {
    setIsEditingValueProposition(false);
    setTempValueProposition(company?.valueProposition || "");
  };

  const handleEditTargetAudience = () => {
    setTempTargetAudience(company?.targetAudience || "");
    setIsEditingTargetAudience(true);
  };

  const handleSaveTargetAudience = () => {
    updateAIFieldMutation.mutate({ targetAudience: tempTargetAudience }, {
      onSuccess: () => {
        setIsEditingTargetAudience(false);
      }
    });
  };

  const handleCancelTargetAudience = () => {
    setIsEditingTargetAudience(false);
    setTempTargetAudience(company?.targetAudience || "");
  };

  const hasAllFields = company?.businessOverview && company?.valueProposition && company?.targetAudience;
  const hasSomeFields = company?.businessOverview || company?.valueProposition || company?.targetAudience;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center space-x-2">
        <Sparkles className="h-6 w-6 text-primary" />
        <h1 className="text-3xl font-bold font-playfair">Marketing Setup</h1>
      </div>

      <Card className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-background">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">
                {isHotel ? "Ihr Marketing-Profil" : "KI-generiertes Marketing-Profil"}
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                {isHotel
                  ? "Diese Angaben hat Ihr Team für Ihr Haus vorbereitet. Sie können sie jederzeit ändern, Ihr Team nutzt sie für Ihre Beiträge."
                  : <>Diese Informationen werden von unserem A-Team automatisch aus Ihrem Profil erstellt 
                und für die Content-Erstellung verwendet.</>}
              </p>
              <div className="flex items-center space-x-2">
                <div className={`w-2 h-2 rounded-full ${hasAllFields ? 'bg-green-500' : hasSomeFields ? 'bg-orange-500 animate-pulse' : 'bg-gray-400'}`}></div>
                <Badge 
                  variant={hasAllFields ? "default" : "outline"} 
                  className={hasAllFields ? "bg-green-500 text-white" : hasSomeFields ? "text-orange-600 border-orange-300" : "text-muted-foreground"}
                >
                  {hasAllFields ? "Vollständig" : hasSomeFields ? "Wird generiert..." : "Ausstehend"}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Building2 className="h-5 w-5 text-primary" />
                <CardTitle>{isHotel ? "Über Ihr Haus" : "Business Overview"}</CardTitle>
                {!isHotel && company?.businessOverview && (
                  <Badge variant="outline" className="text-xs text-primary border-primary/30">
                    KI-generiert
                  </Badge>
                )}
              </div>
              {!isEditingBusinessOverview && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEditBusinessOverview}
                  data-testid="button-edit-business-overview"
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  {company?.businessOverview ? "Bearbeiten" : "Manuell eingeben"}
                </Button>
              )}
            </div>
            <CardDescription>
              Eine ausführliche Übersicht Ihres Hotels für die Content-Erstellung
            </CardDescription>
          </CardHeader>
          <CardContent>
            {company?.businessOverview || isEditingBusinessOverview ? (
              <div className="space-y-3">
                <Textarea
                  data-testid="input-business-overview"
                  value={isEditingBusinessOverview ? tempBusinessOverview : company?.businessOverview || ""}
                  onChange={(e) => setTempBusinessOverview(e.target.value)}
                  className={isEditingBusinessOverview ? "" : "bg-muted/50"}
                  rows={5}
                  disabled={!isEditingBusinessOverview}
                  placeholder="Beschreiben Sie Ihr Hotel, Ihre Angebote und was Sie einzigartig macht"
                />
                {isEditingBusinessOverview && (
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" size="sm" onClick={handleCancelBusinessOverview}>
                      <X className="h-4 w-4 mr-2" />
                      Abbrechen
                    </Button>
                    <Button size="sm" onClick={handleSaveBusinessOverview} disabled={updateAIFieldMutation.isPending}>
                      <Save className="h-4 w-4 mr-2" />
                      {updateAIFieldMutation.isPending ? "Speichert..." : "Speichern"}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <Alert className="border-dashed border-primary/30 bg-primary/5">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                <AlertDescription className="text-muted-foreground">
                  Ihre Übersicht wird gerade von Ihrem A-Team erstellt. 
                  Sie können auch oben auf "Manuell eingeben" klicken, um selbst loszulegen.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                <CardTitle>Wertversprechen</CardTitle>
                {!isHotel && company?.valueProposition && (
                  <Badge variant="outline" className="text-xs text-primary border-primary/30">
                    KI-generiert
                  </Badge>
                )}
              </div>
              {!isEditingValueProposition && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEditValueProposition}
                  data-testid="button-edit-value-proposition"
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  {company?.valueProposition ? "Bearbeiten" : "Manuell eingeben"}
                </Button>
              )}
            </div>
            <CardDescription>
              Ihr Versprechen an Ihre Gäste, das Sie von anderen Häusern abhebt
            </CardDescription>
          </CardHeader>
          <CardContent>
            {company?.valueProposition || isEditingValueProposition ? (
              <div className="space-y-3">
                <Textarea
                  data-testid="input-value-proposition"
                  value={isEditingValueProposition ? tempValueProposition : company?.valueProposition || ""}
                  onChange={(e) => setTempValueProposition(e.target.value)}
                  className={isEditingValueProposition ? "" : "bg-muted/50"}
                  rows={4}
                  disabled={!isEditingValueProposition}
                  placeholder="Was macht Ihr Haus einzigartig? Welchen konkreten Nutzen haben Ihre Gäste?"
                />
                {isEditingValueProposition && (
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" size="sm" onClick={handleCancelValueProposition}>
                      <X className="h-4 w-4 mr-2" />
                      Abbrechen
                    </Button>
                    <Button size="sm" onClick={handleSaveValueProposition} disabled={updateAIFieldMutation.isPending}>
                      <Save className="h-4 w-4 mr-2" />
                      {updateAIFieldMutation.isPending ? "Speichert..." : "Speichern"}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <Alert className="border-dashed border-primary/30 bg-primary/5">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                <AlertDescription className="text-muted-foreground">
                  Ihr Versprechen wird gerade von Ihrem A-Team ausgearbeitet. 
                  Sie können auch oben auf "Manuell eingeben" klicken, um selbst loszulegen.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Target className="h-5 w-5 text-primary" />
                <CardTitle>{isHotel ? "Ihre Gäste" : "Zielgruppe (ICP)"}</CardTitle>
                {!isHotel && company?.targetAudience && (
                  <Badge variant="outline" className="text-xs text-primary border-primary/30">
                    KI-generiert
                  </Badge>
                )}
              </div>
              {!isEditingTargetAudience && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEditTargetAudience}
                  data-testid="button-edit-target-audience"
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  {company?.targetAudience ? "Bearbeiten" : "Manuell eingeben"}
                </Button>
              )}
            </div>
            <CardDescription>
              Ihre Wunschgäste: Wer passt perfekt zu Ihrem Haus?
            </CardDescription>
          </CardHeader>
          <CardContent>
            {company?.targetAudience || isEditingTargetAudience ? (
              <div className="space-y-3">
                <Textarea
                  data-testid="input-target-audience"
                  value={isEditingTargetAudience ? tempTargetAudience : company?.targetAudience || ""}
                  onChange={(e) => setTempTargetAudience(e.target.value)}
                  className={isEditingTargetAudience ? "" : "bg-muted/50"}
                  rows={4}
                  disabled={!isEditingTargetAudience}
                  placeholder="Beschreiben Sie Ihre Wunschgäste: Herkunft, Alter, Reiseanlass, Wünsche"
                />
                {isEditingTargetAudience && (
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" size="sm" onClick={handleCancelTargetAudience}>
                      <X className="h-4 w-4 mr-2" />
                      Abbrechen
                    </Button>
                    <Button size="sm" onClick={handleSaveTargetAudience} disabled={updateAIFieldMutation.isPending}>
                      <Save className="h-4 w-4 mr-2" />
                      {updateAIFieldMutation.isPending ? "Speichert..." : "Speichern"}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <Alert className="border-dashed border-primary/30 bg-primary/5">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                <AlertDescription className="text-muted-foreground">
                  Ihre Zielgruppe wird gerade von Ihrem A-Team beschrieben. 
                  Sie können auch oben auf "Manuell eingeben" klicken, um selbst loszulegen.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
